import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {ActionMenu} from '../action-menu/action-menu';
import type {SelectMenuOption} from '../action-menu/action-menu';
import {EmailLinkedEntities} from '../email-address-links/email-linked-entities';
import {File} from '../files/file';
// @ts-expect-error - imported from .js/.jsx file
import {CampaignIcon} from '../icon/svg/campaign-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from '../icon/svg/eye-icon';
import {StatusBadge} from '../status-badge/status-badge';
import {FormattedTimestamp} from '../timestamp/formatted-timestamp';

import type {SequenceInfo, EmailMessage, EmailStatus} from './types';

import styles from './email-row-item.module.css';

type Props = {
    sequenceInfo?: SequenceInfo | null | undefined;
    email: EmailMessage;
    status?: EmailStatus;
    actions: SelectMenuOption[];
    onCollapseRow: () => any;
    detailLinkComponent?: React.ReactNode;
    deletingDialog: React.ReactNode | null | undefined;
    navToSequenceDetails?: () => void;
    // Can't use CSS psuedo selectors here (without some hacks)
    // so we'll pass in a prop to toggle our top-border if this
    // row is the first of its date.
    isFirstOfDate: boolean;
    getRef: (arg1?: HTMLElement | null | undefined) => void;
};

export class EmailRowItemExpanded extends React.PureComponent<Props> {
    actionMenu: HTMLElement | null | undefined;

    static defaultProps = {
        getRef: () => {},
    };

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    render() {
        const {email, status, sequenceInfo} = this.props;
        const styleNames = classnames({
            [styles['email-row-item--expanded']]: true,
            [styles['email-row-item--hidden']]:
                this.props.status && this.props.status.level === 'hidden',
            [styles['email-row-item--first-of-date']]: this.props.isFirstOfDate,
        });

        return (
            <div className={styleNames}>
                <div
                    onClick={this.props.onCollapseRow}
                    className={styles['expanded-header-container']}
                >
                    <div className={styles['subject-header']}>{email.subject}</div>
                    {status ? (
                        <div className={styles['status-badge-expanded']}>
                            <StatusBadge {...status} />
                        </div>
                    ) : undefined}
                    {email.trackedViewsCount && email.trackedViewsCount > 0
                        ? this.renderViewCount(email.trackedViewsCount)
                        : undefined}
                    {sequenceInfo ? this.renderSequenceInfo(sequenceInfo) : undefined}
                    {this.renderHeaderRight(email)}
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        {this.props.deletingDialog}
                    </div>
                </div>
                <div className={styles['expanded-content-container']}>
                    {this.renderRecipientsList(email)}
                    {this.renderExpandedContent(email)}
                    {this.renderAttachmentList(email)}
                </div>
            </div>
        );
    }

    renderRecipientsList(email: EmailMessage) {
        return (
            <div className={styles['recipients-list']}>
                <div className={styles['sender-row']}>
                    From:&nbsp;
                    {/* @ts-expect-error */}
                    <EmailLinkedEntities
                        useEntityColor={true}
                        emailAddressees={[email.from]}
                        showAddress={true}
                        showAll={false}
                    />
                </div>
                <div className={styles['recipients-row']}>
                    To:&nbsp;
                    {/* @ts-expect-error */}
                    <EmailLinkedEntities
                        useEntityColor={true}
                        emailAddressees={email.to}
                        showAddress={true}
                        showAll={true}
                    />
                </div>
                {email.cc.length ? (
                    <div className={styles['recipients-row']}>
                        Cc:&nbsp;
                        {/* @ts-expect-error */}
                        <EmailLinkedEntities
                            useEntityColor={true}
                            emailAddressees={email.cc}
                            showAddress={true}
                            showAll={true}
                        />
                    </div>
                ) : null}
            </div>
        );
    }

    renderExpandedContent(email: EmailMessage) {
        return (
            <div className={styles['email-contents-container']}>
                <div className={styles['email-contents']}>
                    <span className={styles['email-content-text']}>
                        <span
                            dangerouslySetInnerHTML={{__html: email.bodyHtml}}
                            className={styles['body-container']}
                        />
                    </span>
                </div>
                {this.props.detailLinkComponent ? (
                    <div className={styles['details-link']}>{this.props.detailLinkComponent}</div>
                ) : undefined}
            </div>
        );
    }

    renderViewCount(trackedViews: number) {
        return (
            <div
                tabIndex={0}
                data-tip={trackedViews === 1 ? '1 view' : `${trackedViews} views`}
                data-event='mouseenter focus'
                data-event-off='mouseleave blur click'
                className={styles['inline-icon-container']}
            >
                <span className={styles['tracked-view-count']}>{trackedViews}&nbsp;</span>
                <EyeIcon size={15} wrapWithDiv={false} className={styles['inline-icon']} />
            </div>
        );
    }

    renderSequenceInfo(sequenceInfo: SequenceInfo) {
        return (
            <button
                type='button'
                onClick={(e) => {
                    e.stopPropagation();
                    if (this.props.navToSequenceDetails) {
                        this.props.navToSequenceDetails();
                    }
                }}
                tabIndex={0}
                data-tip={
                    sequenceInfo && sequenceInfo.messageNumber
                        ? `Message ${sequenceInfo.messageNumber} of ${sequenceInfo.messageCount} in ${sequenceInfo.name}`
                        : undefined
                }
                data-event='mouseenter focus'
                data-event-off='mouseleave blur click'
                className={styles['inline-icon-container']}
            >
                <CampaignIcon size={15} wrapWithDiv={false} className={styles['inline-icon']} />
            </button>
        );
    }

    renderHeaderRight(email: EmailMessage) {
        return (
            <div className={styles['header-right']}>
                <div className={styles['timestamp-container']}>
                    <FormattedTimestamp
                        format={FormattedTimestamp.formats.time}
                        datetime={email.timestamp}
                    />
                </div>
                {this.props.actions.length > 0 ? (
                    <ActionMenu getRef={this.props.getRef} options={this.props.actions} />
                ) : undefined}
            </div>
        );
    }

    renderAttachmentList(email: EmailMessage) {
        return (
            <ul className={styles['file-list']}>
                {email.attachments.map((fileAttachment: any) => {
                    return (
                        <File
                            key={fileAttachment.fileId}
                            id={fileAttachment.fileId}
                            href={fileAttachment.href}
                            filetype={fileAttachment.filetype}
                            filename={fileAttachment.name}
                        />
                    );
                })}
            </ul>
        );
    }
}
